import { CdkScrollable } from '@angular/cdk/scrolling';
import { DatePipe, NgClass } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit, output, TemplateRef, viewChild } from '@angular/core';
import { MatDialog, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import {
  Address,
  AvailabilityDto,
  ENVIRONMENT,
  Environment,
  InputActivity,
  LeadAvailabilityDayDto,
  LeadWeekAvailabilityDto,
  PartOfDay,
} from '@domains';
import { LeadsService } from '@rspl-api';
import * as moment from 'moment';
import { take } from 'rxjs/operators';

import { FxFlexDirective, FxLayoutAlignDirective, FxLayoutDirective, FxLayoutGapDirective } from '../flex-layout';
import { InfoBoxComponent } from '../info-box';
import { Responsive, ResponsiveService } from '../responsive';

/** `Number(undefined)` is NaN, which slips past every `=== undefined` guard downstream. */
function toWeekNumber(value: number | undefined): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export interface LeadAvailabilityZone {
  in_vehicle_zone: boolean;
  in_store_zone: boolean;
  /**
   * A courier week: the days carry hourly slots instead of AM/PM, and the zone flags
   * above are not reported. Covers Roadie as well as Uber — `/leads/availability`
   * picks whichever provider can serve the address.
   */
  courier: boolean;
  /** The charity requires a zone this address falls outside of — the block is final, not a warning. */
  in_zone_required: boolean;
}

/**
 * Week calendar backed by `/leads/availability` (see `LeadsService.getAvailability`).
 *
 * The internal-actor counterpart to the donor-facing `AvailabilityComponent`, which
 * still calls the older `/donations/availability` pair. Two components rather than a
 * mode flag on one, because the endpoints do not just differ in URL: the new one
 * decides courier eligibility server-side, reports it as `courier` (not `uber`),
 * returns hourly capacity as `am_slots`/`pm_slots` (no daily `am`/`pm` flags), and
 * OMITS the zone flags entirely on a courier week. Carrying both contracts in one
 * class would mean reasoning about every future change twice.
 *
 * Scope is the captain surfaces only, so none of the donor design variants,
 * out-of-zone dialog or `isDonor` branching come along.
 */
@Component({
  selector: 'rspl-lead-availability',
  templateUrl: './lead-availability.component.html',
  styleUrls: ['./lead-availability.component.scss'],
  imports: [
    FxLayoutDirective,
    FxFlexDirective,
    FxLayoutAlignDirective,
    FxLayoutGapDirective,
    NgClass,
    InfoBoxComponent,
    MatDialogTitle,
    MatIcon,
    CdkScrollable,
    MatDialogContent,
    DatePipe,
  ],
})
export class LeadAvailabilityComponent extends Responsive implements OnInit, OnDestroy {
  private isDestroyed = false;

  readonly availableHoursTemplate = viewChild.required<TemplateRef<any>>('AvailableHours');

  outOfZone = false;
  inZoneRequired = false;
  public week?: number;
  public year?: number;
  public dates: AvailabilityDto[] = [];
  public isLoaded = false;
  pastDate = false;

  @Input() disableDates: string[] = [];
  @Input() disabled = false;
  @Input() disableActivities = false;
  @Input() showError = false;

  #selectedDate: { date?: string; partOfDay?: PartOfDay; hour_slot?: number } = {};
  @Input() set selectedDate(selectedDate: { date?: string; partOfDay?: PartOfDay; hour_slot?: number }) {
    const fetchWeek = !this.#selectedDate.date && !this.#selectedDate.partOfDay;
    this.#selectedDate = selectedDate;
    if (this.selectedDate.date && this.selectedDate.partOfDay) {
      const day = this.dates.find((d) => d.date === this.selectedDate.date);
      this.selectedAvailable = day ? !!day[this.selectedDate.partOfDay] : this.selectedAvailable;
      this.pastDate = !moment().hours(0).minutes(0).seconds(0).milliseconds(0).isSameOrBefore(moment(this.selectedDate.date));
      if (!this.pastDate && fetchWeek) {
        this.week = moment(this.selectedDate.date).week();
        this.year = moment(this.selectedDate.date).weekYear();
        if (!this.dates?.find((d) => d.date === this.selectedDate.date)) {
          this.getAvailability(false);
        }
      }
    }
  }

  get selectedDate(): { date?: string; partOfDay?: PartOfDay; hour_slot?: number } {
    return this.#selectedDate;
  }

  #charityId?: string | null;
  @Input() set charityId(charityId: string | null | undefined) {
    this.#charityId = charityId?.toString() || null;
    if (this.address?.zip) {
      this.getAvailability(false);
    }
  }

  get charityId(): string | null | undefined {
    return this.#charityId;
  }

  #partnerId?: string | null;
  @Input() set partnerId(partnerId: string | null | undefined) {
    this.#partnerId = partnerId?.toString() || null;
    if (this.address?.zip) {
      this.getAvailability(false);
    }
  }

  get partnerId(): string | null | undefined {
    return this.#partnerId;
  }

  /**
   * The whole address, not just the zip — the courier providers behind
   * `/leads/availability` need the coordinates to find a store in range, so a
   * street change can flip a week between daily and hourly slots.
   *
   * Keyed on the address VALUE, not the object: callers hand a fresh `Address`
   * on every patch, and re-fetching on each of those would put a request (and a
   * first-available activity) behind every unrelated form change.
   */
  #address?: Address;
  @Input() set address(address: Address | undefined) {
    const previousKey = this.addressKey(this.#address);
    const previousZip = this.#address?.zip;
    this.#address = address;

    if (!address?.zip) {
      this.setEmptyAvailability();
      return;
    }
    if (previousKey === this.addressKey(address)) return;

    // Only a different zip invalidates the pick — the same place with a corrected
    // street keeps it, and the parent re-applies the lead's own date either way.
    if (previousZip && previousZip !== address.zip) {
      this.selectedDate = { date: null, partOfDay: null, hour_slot: null };
    }
    this.getAvailability(true);
  }

  private addressKey(address?: Address): string {
    if (!address?.zip) return '';
    return [address.street, address.secondary, address.city, address.state, address.zip, address.lat, address.lng]
      .map((part) => part ?? '')
      .join('|');
  }

  get address(): Address | undefined {
    return this.#address;
  }

  get zip(): string | undefined {
    return this.#address?.zip;
  }

  @Input() leadId?: string;

  readonly dateSelected = output<{ date?: string; partOfDay?: PartOfDay; hour_slot?: number }>();
  readonly zoneChange = output<LeadAvailabilityZone>();

  partsOfDay = PartOfDay;
  minDate = moment().add(-1, 'days').seconds(59).minutes(59).hours(23);
  minWeek = moment().week();
  minYear = moment().weekYear();
  weekAvailability?: LeadWeekAvailabilityDto;
  showHourlySlots = false;
  selectedAvailable = true;

  availableHoursDialog?: MatDialogRef<any, any>;
  hoursDialogData: { date: string; partOfDay: PartOfDay; available: any; hour_slot?: number };

  constructor(
    private leadsService: LeadsService,
    private dialog: MatDialog,
    public override responsiveService: ResponsiveService,
    @Inject(ENVIRONMENT) protected config: Environment,
  ) {
    super(responsiveService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.setEmptyAvailability();
  }

  private getAvailability(logFirst = false) {
    if (this.isDestroyed || !this.address?.zip) return;
    this.leadsService
      .getAvailability({
        address: this.address,
        charityId: this.charityId,
        partnerId: this.partnerId,
        leadId: this.leadId,
        week: this.week,
        year: this.year,
      })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (this.isDestroyed) return;
          this.week = toWeekNumber(response.week);
          this.year = toWeekNumber(response.year);
          this.setZoneAvailability(response);
          this.logFirst(logFirst);
          if (this.selectedDate.date && this.selectedDate.partOfDay)
            this.onSelectDate(this.selectedDate.date, this.selectedDate.partOfDay, this.selectedDate.hour_slot);
        },
        // The week buttons and every day are disabled while `isLoaded` is false, so a
        // failed request would otherwise freeze the calendar until a page reload.
        // Hand control back and let the agent retry by navigating weeks.
        error: () => {
          if (this.isDestroyed) return;
          this.isLoaded = true;
        },
      });
  }

  setZoneAvailability(availability: LeadWeekAvailabilityDto, logActivity = true, isLoaded = true, emitZone = true) {
    const isCourier = availability.courier === true;
    this.weekAvailability = availability;
    this.dates = this.normalizeDays(availability.available ?? []);
    // Keyed on the response, not on `some(day => !!day.slots)`: `normalizeDays` gives a
    // fully-booked courier day an EMPTY slots array, which is truthy — so a week with no
    // hours left read as hourly on some days and daily on others.
    this.showHourlySlots = isCourier;

    // Zone flags are ABSENT on a courier week, so every test is against an explicit
    // `false`. Treating `undefined` as "out" would read every courier lead as out of zone.
    const inStoreZone = availability.in_store_zone !== false;
    const inVehicleZone = availability.in_vehicle_zone !== false;
    const inZoneRequired = availability.in_zone_required === true;
    const outOfZone = !isCourier && (!inVehicleZone || (!!this.charityId && !inStoreZone));

    if (!this.disableActivities && this.leadId) {
      if (!inVehicleZone) this.leadsService.createLeadActivity(this.leadId, InputActivity.OUT_OF_ZONE_VEHICLE, this.zip).subscribe();
      if (!inStoreZone) this.leadsService.createLeadActivity(this.leadId, InputActivity.OUT_OF_ZONE_STORE, this.zip).subscribe();
      if (logActivity && !outOfZone) this.leadsService.createLeadActivity(this.leadId, InputActivity.IN_ZONE, this.zip).subscribe();
    }

    this.isLoaded = isLoaded;
    this.outOfZone = outOfZone;
    this.inZoneRequired = inZoneRequired;

    if (this.zip && emitZone) {
      this.zoneChange.emit({
        in_vehicle_zone: inVehicleZone,
        in_store_zone: inStoreZone,
        courier: isCourier,
        in_zone_required: inZoneRequired,
      });
    }
  }

  /**
   * Fold the endpoint's hourly shape into the daily one the template renders:
   * `am_slots`/`pm_slots` become one `slots` list tagged with its part of day, and
   * the `am`/`pm` flags — which a courier day does not carry — are derived from
   * slot presence.
   */
  private normalizeDays(days: LeadAvailabilityDayDto[]): AvailabilityDto[] {
    return days.map((day) => {
      if (!day.am_slots && !day.pm_slots) return day;

      const amSlots = (day.am_slots ?? []).map((slot) => ({ ...slot, part_of_day: slot.part_of_day ?? PartOfDay.am }));
      const pmSlots = (day.pm_slots ?? []).map((slot) => ({ ...slot, part_of_day: slot.part_of_day ?? PartOfDay.pm }));

      return {
        ...day,
        am: day.am ?? amSlots.length > 0,
        pm: day.pm ?? pmSlots.length > 0,
        slots: [...amSlots, ...pmSlots],
      };
    });
  }

  nextWeek(numOfWeeks = 1, logFirst = false): void {
    if (this.year === undefined || this.week === undefined) return;
    this.isLoaded = false;

    const newWeek = moment()
      .date(1)
      .month(0)
      .year(this.year)
      .add(this.week - 1, 'weeks')
      .endOf('week')
      .add(1, 'hour')
      .add(7 * (numOfWeeks - 1), 'days');
    this.week = newWeek.week();
    this.year = newWeek.weekYear();
    this.getAvailability(logFirst);
  }

  previousWeek(numOfWeeks = 1): void {
    if (this.year === undefined || this.week === undefined || (this.week <= this.minWeek && this.minYear === this.year)) return;
    this.isLoaded = false;
    const newWeek = moment()
      .date(1)
      .month(0)
      .year(this.year)
      .add(this.week - 1, 'weeks')
      .startOf('week')
      .add(-7 * numOfWeeks, 'days');
    this.week = newWeek.week();
    this.year = newWeek.weekYear();
    this.getAvailability(false);
  }

  selectDate(date: string, partOfDay: PartOfDay): void {
    if (!this.showHourlySlots) {
      this.onSelectDate(date, partOfDay);
      return;
    }

    this.hoursDialogData = {
      date,
      partOfDay,
      available: this.dates.find((d) => d.date === date)?.slots?.filter((s) => s.part_of_day === partOfDay) || [],
      hour_slot: undefined,
    };
    this.availableHoursDialog = this.dialog.open(this.availableHoursTemplate(), {
      width: '500px',
      maxWidth: '95vw',
      panelClass: 'availability-hours-dialog',
    });
    this.availableHoursDialog
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (res && this.hoursDialogData.hour_slot) {
          this.onSelectDate(date, partOfDay, this.hoursDialogData.hour_slot);
        } else {
          this.hoursDialogData = undefined;
        }
      });
  }

  onSelectDate(date: string, partOfDay: PartOfDay, slot?: number) {
    this.selectedDate = {};
    this.selectedDate.date = date;
    this.selectedDate.partOfDay = partOfDay;
    this.selectedDate.hour_slot = slot;
    const day = this.dates.find((d) => d.date === date);
    this.selectedAvailable = day ? !!day[partOfDay] : this.selectedAvailable;
    this.pastDate = !moment().hours(0).minutes(0).seconds(0).milliseconds(0).isSameOrBefore(moment(this.selectedDate.date));
    this.dateSelected.emit(this.selectedDate);
  }

  setEmptyAvailability() {
    const weekStart = (this.year !== undefined && this.week !== undefined ? moment().year(this.year).week(this.week) : moment()).startOf(
      'week',
    );
    this.setZoneAvailability(
      {
        in_vehicle_zone: true,
        in_store_zone: true,
        available: [0, 1, 2, 3, 4, 5, 6].map((i) => ({
          date: moment(weekStart).add(i, 'days').format('YYYY-MM-DD'),
          am: false,
          pm: false,
        })),
        courier: false,
      },
      false,
      false,
      false,
    );
  }

  beforeMin(date: string): boolean {
    return moment(date).seconds(0).minutes(0).hours(0).isBefore(this.minDate);
  }

  private logFirst(logFirst: boolean) {
    if (!logFirst || !this.leadId || this.disableActivities) return;
    const firstAvailable = this.getFirstAvailable();
    if (firstAvailable) {
      this.leadsService.createLeadActivity(this.leadId, InputActivity.FIRST_AVAILABLE_DATE, firstAvailable).subscribe();
    }
  }

  private getFirstAvailable(): string | null {
    for (const day of this.dates) {
      if (!this.beforeMin(day.date) && (day.am || day.pm)) {
        return day.date;
      }
    }
    return null;
  }

  /**
   * The picked time as one string: an hour on a courier week, the AM/PM window on a daily
   * one, and `/` when there is nothing yet — the row renders unconditionally, so without
   * the fallback it sits blank. Matches how `Address.fullAddress` marks "nothing here".
   */
  get selectedTimeLabel(): string {
    const { partOfDay, hour_slot } = this.selectedDate;
    if (hour_slot) return `${this._24hTo12h(hour_slot)}:00 ${partOfDay?.toUpperCase() ?? ''}`.trim();
    if (!this.showHourlySlots && partOfDay === PartOfDay.am) return '08:00 AM - 12:00 PM';
    if (!this.showHourlySlots && partOfDay === PartOfDay.pm) return '12:00 PM - 04:00 PM';
    return '/';
  }

  _24hTo12h(hour: number): number {
    return hour > 12 ? hour - 12 : hour;
  }

  override ngOnDestroy(): void {
    this.isDestroyed = true;
    super.ngOnDestroy();
  }
}
